import { ReactNode } from "react";

import { HighlightedAppLocationViewCounts } from "../api/faq/getHighlightedAppLocationViews";
import { HighlightedFaqViewCounts } from "../api/faq/getHighlightedFaqViews";
import { FaqContextIds } from "../components/AppLocation";
import { CRMAppLocationIdentifier, FaqAppLocation } from "../types/Faq";
import { getNestedProperty } from "./object";
import { addParamToHref } from "./routes";

const HIGHLIGHTED_FAQ_VIEW_MAX = 2;
const HIGHLIGHTED_APP_LOCATION_VIEW_MAX = 2;

export type AppLocationRenderFunction = (
  appLocationIdentifier: CRMAppLocationIdentifier,
  children: ReactNode,
  hideIfNoFaqs?: boolean,
  hideHighlight?: boolean,
  triggerOnLoad?: boolean,
  persistentModal?: boolean
) => JSX.Element;

export const hasHighlightedFaqs = (
  appLocations: FaqAppLocation[],
  appLocationIdentifiers: CRMAppLocationIdentifier[],
  highlightedFaqViewCounts: HighlightedFaqViewCounts
): boolean => {
  return appLocations.some((location) => {
    return (
      appLocationIdentifiers.includes(location.appLocationIdentifier) &&
      !!location.highlightedFaq &&
      (!highlightedFaqViewCounts[location.highlightedFaq.id] ||
        highlightedFaqViewCounts[location.highlightedFaq.id] <
          HIGHLIGHTED_FAQ_VIEW_MAX)
    );
  });
};

export const hasHighlightedAppLocationViews = (
  appLocations: FaqAppLocation[],
  appLocationIdentifiers: CRMAppLocationIdentifier[],
  highlightedAppLocationViewCounts: HighlightedAppLocationViewCounts
): boolean => {
  return appLocations.some((location) => {
    return (
      appLocationIdentifiers.includes(location.appLocationIdentifier) &&
      !!location.highlightedText &&
      (!highlightedAppLocationViewCounts[location.appLocationIdentifier] ||
        (highlightedAppLocationViewCounts[location.appLocationIdentifier] ??
          0) < HIGHLIGHTED_APP_LOCATION_VIEW_MAX)
    );
  });
};

export const addFaqModalParamsToHref = (
  href: string,
  appLocationId: string,
  contextIds?: FaqContextIds,
  persistentModal?: boolean
) => {
  let linkTo = addParamToHref("modal.appLocationId", appLocationId, href);

  if (contextIds?.quoteId) {
    // Add quote id modal param
    linkTo = addParamToHref("modal.quoteId", contextIds.quoteId, linkTo);
  }

  if (contextIds?.opportunityId) {
    linkTo = addParamToHref(
      "modal.opportunityId",
      contextIds.opportunityId,
      linkTo
    );
  }

  if (contextIds?.dossierId) {
    linkTo = addParamToHref("modal.dossierId", contextIds.dossierId, linkTo);
  }

  if (persistentModal) {
    linkTo = addParamToHref("modal.type", "persistent", linkTo);
  }

  return linkTo;
};

export const addFaqParamsToSearchParams = (
  params: URLSearchParams,
  appLocationId: string,
  faqAppLocationId: string | null,
  contextIds?: FaqContextIds
) => {
  params.append("modal.appLocationId", appLocationId);

  if (faqAppLocationId) {
    params.append("modal.faqAppLocationId", faqAppLocationId);
  }

  if (contextIds?.quoteId) {
    params.append("modal.quoteId", contextIds.quoteId);
  }

  if (contextIds?.opportunityId) {
    params.append("modal.opportunityId", contextIds.opportunityId);
  }

  return params;
};

export const compileTemplate = (
  template: string,
  values: Record<string, unknown>,
  valuePrefix?: string
): string => {
  const compliedTemplate = template.replace(/\{\{([^{}]+)\}\}/g, (_, key) => {
    const valueKey = valuePrefix ? key.replace(`${valuePrefix}.`, "") : key;
    const value = getNestedProperty(valueKey, values);

    return value || value === 0 ? `${value}` : "";
  });

  return compliedTemplate;
};
